import { useEffect } from "react";

// Radix still owns focus, dismissal and wheel/touch isolation. Only its
// inline <style> scrollbar helper is replaced by our same-origin stylesheet.
export const zeroRightClassName = "right-scroll-bar-position";
export const fullWidthClassName = "width-before-scroll-bar";
export const noScrollbarsClassName = "with-scroll-bars-hidden";
export const removedBarSizeVariable = "--removed-body-scroll-bar-size";
type GapMode = "margin" | "padding";
type Props = { noRelative?: boolean; noImportant?: boolean; gapMode?: GapMode };

export function getGapWidth(gapMode: GapMode = "margin") {
  if (typeof window === "undefined") return { left: 0, top: 0, right: 0, gap: 0 };
  const style = window.getComputedStyle(document.body);
  const value = (side: "Left" | "Top" | "Right") =>
    Number.parseInt(style[`${gapMode}${side}`], 10) || 0;
  const left = value("Left");
  const top = value("Top");
  const right = value("Right");
  return {
    left,
    top,
    right,
    gap: Math.max(0, window.innerWidth - document.documentElement.clientWidth + right - left),
  };
}

const attributeNames = [
  "data-scroll-locked",
  "data-cyra-scroll-lock",
  "data-cyra-scroll-lock-relative",
  "data-cyra-scroll-lock-plain",
] as const;
const variableNames = [
  removedBarSizeVariable,
  "--cyra-scroll-lock-left",
  "--cyra-scroll-lock-top",
  "--cyra-scroll-lock-right",
] as const;
type Lease = { count: number; baselineCount: number; restore: () => void };
const leases = new WeakMap<HTMLElement, Lease>();

function acquire({ gapMode = "margin", noRelative = false, noImportant = false }: Props) {
  const body = document.body;
  const active = leases.get(body);
  if (active) {
    active.count += 1;
    body.setAttribute("data-scroll-locked", String(active.baselineCount + active.count));
  } else {
    // Measure once before locking; nested menus must not replace the gap with zero.
    const gap = getGapWidth(gapMode);
    const attributes = attributeNames.map((name) => [name, body.getAttribute(name)] as const);
    const properties = variableNames.map(
      (name) =>
        [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)] as const,
    );
    for (const [name, value] of [
      [removedBarSizeVariable, gap.gap],
      ["--cyra-scroll-lock-left", gap.left],
      ["--cyra-scroll-lock-top", gap.top],
      ["--cyra-scroll-lock-right", gap.right],
    ] as const) {
      // CSSStyleDeclaration property assignments work under style-src 'self'.
      // Values come only from measured numeric offsets, never user input.
      body.style.setProperty(name, `${value}px`);
    }
    const baselineCount = Number.parseInt(body.getAttribute("data-scroll-locked") ?? "0", 10) || 0;
    body.setAttribute("data-scroll-locked", String(baselineCount + 1));
    body.setAttribute("data-cyra-scroll-lock", gapMode);
    body.toggleAttribute("data-cyra-scroll-lock-relative", !noRelative);
    body.toggleAttribute("data-cyra-scroll-lock-plain", noImportant);
    leases.set(body, {
      count: 1,
      baselineCount,
      restore() {
        for (const [name, value] of attributes) {
          if (value === null) body.removeAttribute(name);
          else body.setAttribute(name, value);
        }
        for (const [name, value, priority] of properties) {
          if (value) body.style.setProperty(name, value, priority);
          else body.style.removeProperty(name);
        }
      },
    });
  }
  return () => {
    const lease = leases.get(body);
    if (!lease) return;
    lease.count -= 1;
    if (lease.count)
      body.setAttribute("data-scroll-locked", String(lease.baselineCount + lease.count));
    else {
      lease.restore();
      leases.delete(body);
    }
  };
}

export function RemoveScrollBar(props: Props) {
  const { gapMode = "margin", noRelative = false, noImportant = false } = props;
  useEffect(
    () => acquire({ gapMode, noRelative, noImportant }),
    [gapMode, noRelative, noImportant],
  );
  return null;
}
